/** @format */

import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getMissedContributions } from "../../redux/actions/autoDraftAction";
import { MissedContributionsModal } from "./MissedContributionsModal";
import { BouncingNotificationBadge } from "./BouncingNotificationBadge";
import { AppDispatch, RootState } from "../../redux/store/store";

interface MissedContributionsRenderProps {
	hasMissedContributions: boolean;
	missedCount: number;
	totalDueDollars: number;
	openMissedContributions: () => void;
}

interface MissedContributionsWrapperProps {
	children:
		| React.ReactNode
		| ((props: MissedContributionsRenderProps) => React.ReactNode);
	unionId?: string; // Optional: filter to show only this circle's missed contributions
	fullWidth?: boolean; // Stretch to the container instead of wrapping the button inline
	showBadge?: boolean; // Set false when the child already surfaces missed contributions
	onContributeClick?: (
		unionId: string,
		totalDueDollars: number,
		missedDates: string[],
		isContributionDayToday: boolean,
		isTodayAlreadyPaid: boolean,
	) => void;
}

/**
 * Wrapper component that adds the bouncing notification badge
 * and missed contributions modal to any button
 *
 * Usage with render props (to disable button when there are missed contributions):
 * <MissedContributionsWrapper unionId={circleId} onContributeClick={handleContribute}>
 *   {({ hasMissedContributions, missedCount }) => (
 *     <ContributeBtn disabled={hasMissedContributions} />
 *   )}
 * </MissedContributionsWrapper>
 *
 * Or simple usage:
 * <MissedContributionsWrapper unionId={circleId}>
 *   <Button>Contribute</Button>
 * </MissedContributionsWrapper>
 */
export const MissedContributionsWrapper: React.FC<
	MissedContributionsWrapperProps
> = ({
	children,
	unionId,
	onContributeClick,
	fullWidth = false,
	showBadge = true,
}) => {
	const [isModalOpen, setIsModalOpen] = useState(false);
	const dispatch = useDispatch<AppDispatch>();

	const { missedContributions, totalMissedCount, isLoading } = useSelector(
		(state: RootState) => state.autoDraft,
	);

	// Fetch missed contributions on mount and refresh every hour
	useEffect(() => {
		dispatch(getMissedContributions());

		const intervalId = setInterval(() => {
			dispatch(getMissedContributions());
		}, 3600000); // 1 hour in milliseconds (3600 * 1000)

		return () => clearInterval(intervalId);
	}, [dispatch]);

	const handleBadgeClick = () => {
		setIsModalOpen(true);
	};

	const handleContribute = (clickedUnionId: string) => {
		setIsModalOpen(false);
		if (onContributeClick) {
			// Find the missed contribution for this union to get the total due and missed dates
			const missedContribution = missedContributions.find(
				(mc) => mc.unionId === clickedUnionId,
			);
			const totalDueDollars = missedContribution?.totalDueDollars || 0;
			const missedDates = missedContribution?.unpaidDates || [];
			const isContributionDayToday =
				missedContribution?.isContributionDayToday || false;
			const isTodayAlreadyPaid =
				missedContribution?.isTodayAlreadyPaid || false;
			onContributeClick(
				clickedUnionId,
				totalDueDollars,
				missedDates,
				isContributionDayToday,
				isTodayAlreadyPaid,
			);
		}
	};

	// Filter to only this circle's missed contributions if unionId is provided
	const filteredMissedContributions =
		unionId ?
			missedContributions.filter((mc) => mc.unionId === unionId)
		:	missedContributions;

	const filteredMissedCount = filteredMissedContributions.reduce(
		(sum, mc) => sum + mc.missedCount,
		0,
	);

	const hasMissedContributions = filteredMissedCount > 0;

	const filteredTotalDueDollars = filteredMissedContributions.reduce(
		(sum, mc) => sum + (mc.totalDueDollars || 0),
		0,
	);

	const wrapperClassName =
		fullWidth ? "relative block w-full" : "relative inline-block";

	if (isLoading && missedContributions.length === 0) {
		// Render children with no missed contributions state
		const renderedChildren =
			typeof children === "function" ?
				children({
					hasMissedContributions: false,
					missedCount: 0,
					totalDueDollars: 0,
					openMissedContributions: handleBadgeClick,
				})
			:	children;
		return fullWidth ?
				<div className={wrapperClassName}>{renderedChildren}</div>
			:	<>{renderedChildren}</>;
	}

	// Render children - either as render props function or direct element
	const renderedChildren =
		typeof children === "function" ?
			children({
				hasMissedContributions,
				missedCount: filteredMissedCount,
				totalDueDollars: filteredTotalDueDollars,
				openMissedContributions: handleBadgeClick,
			})
		:	children;

	return (
		<div className={wrapperClassName}>
			{/* Original button */}
			{renderedChildren}

			{/* Bouncing notification badge */}
			{showBadge && filteredMissedCount > 0 && (
				<BouncingNotificationBadge
					count={filteredMissedCount}
					onClick={handleBadgeClick}
				/>
			)}

			{/* Modal */}
			<MissedContributionsModal
				isOpen={isModalOpen}
				onClose={() => setIsModalOpen(false)}
				missedContributions={filteredMissedContributions}
				onContributeClick={handleContribute}
			/>
		</div>
	);
};
